import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { DataTable, IconReports, Pagination } from '@ui';
import type { DataTableColumn, SortDirection, SortType } from '@ui';
import { fetchMergedInspectionReports, ReportFilterCriteria } from '../../graphql/reportQueries';
import { ReportFilterBar, ReportFilterValues, UnitReportScope } from './ReportFilterBar';
import { ReportExportMenu } from './ReportExportMenu';
import { useRegionAndLocationContext } from '../../contexts/RegionAndLocationContext';
import { buildReportPath } from '../../utils/reportUrlUtils';
import {
    buildCompanyReportsPath,
    buildUnitReportsPath,
    type ReportDetailReturnState,
} from '../../utils/unitReportsUtils';
import type { NormalizedReportRecord, ReportConfiguration, AggregatedDataPoint } from '../../types/reportTypes';
import '../../css/InspectionReportsTab.css';

interface InspectionReportsTabProps {
    companyId: string;
    unitScope?: UnitReportScope;
}

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50];
const DEFAULT_SORT_COLUMN_ID = 'date';

const compareReportRows = <T,>(
    a: T,
    b: T,
    sortValue: (row: T) => string | number,
    sortType: SortType,
    direction: SortDirection,
) => {
    const aVal = sortValue(a);
    const bVal = sortValue(b);
    let result = 0;

    if (sortType === 'numeric') {
        const aNum = typeof aVal === 'number' ? aVal : Number(aVal);
        const bNum = typeof bVal === 'number' ? bVal : Number(bVal);
        result = aNum - bNum;
    } else {
        result = String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: 'base' });
    }

    return direction === 'desc' ? -result : result;
};

const formatDate = (dateStr: string) => {
    if (!dateStr) return '—';
    try {
        return new Date(dateStr).toLocaleDateString();
    } catch {
        return dateStr;
    }
};

const formatDuration = (seconds?: number) => {
    if (seconds == null) return '—';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins === 0) return `${secs}s`;
    return `${mins}m ${secs}s`;
};

const getDateSortValue = (dateStr: string) => {
    if (!dateStr) return 0;
    const timestamp = Date.parse(dateStr);
    return Number.isNaN(timestamp) ? 0 : timestamp;
};

/**
 * Inspection Reports tab content.
 * Fetches merged inspection reports (new + legacy), displays in a table
 * with filtering, pagination, row click navigation, and export.
 *
 * Validates: Requirements 4.1, 4.2, 4.3, 4.4, 4.5, 4.6, 4.7, 4.8, 9.6
 */
export const InspectionReportsTab = ({ companyId, unitScope }: InspectionReportsTabProps) => {
    const navigate = useNavigate();
    const tableRef = useRef<HTMLDivElement>(null);
    const { companyLocations } = useRegionAndLocationContext();

    const [records, setRecords] = useState<NormalizedReportRecord[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [sortColumnId, setSortColumnId] = useState(DEFAULT_SORT_COLUMN_ID);
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

    const STORAGE_KEY = 'report-filters-inspection';

    // Default to last 30 days for initial load performance
    const defaultStart = useMemo(() => {
        const d = new Date();
        d.setDate(d.getDate() - 30);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }, []);

    // Initialize from sessionStorage so the first fetch uses restored filters
    const [filters, setFilters] = useState<ReportFilterValues>(() => {
        try {
            const saved = sessionStorage.getItem(STORAGE_KEY);
            if (saved) return JSON.parse(saved) as ReportFilterValues;
        } catch {
            /* ignore */
        }
        return {
            equipmentCategoryId: '',
            unitId: '',
            dateStart: defaultStart,
            dateEnd: '',
            locationId: '',
            districtId: '',
            regionId: '',
        };
    });

    // Fetch reports when companyId or date filters change (server-side = date range only)
    useEffect(() => {
        if (!companyId) return;
        setIsLoading(true);
        setError(null);

        const criteria: ReportFilterCriteria = {};
        if (filters.dateStart) criteria.dateStart = filters.dateStart;
        if (filters.dateEnd) criteria.dateEnd = filters.dateEnd;

        fetchMergedInspectionReports(companyId, criteria)
            .then((items) => setRecords(items))
            .catch((err) => {
                console.error('Failed to fetch inspection reports:', err);
                Sentry.captureException(err);
                setError('Failed to load inspection reports. Please try again.');
            })
            .finally(() => setIsLoading(false));
    }, [companyId, filters.dateStart, filters.dateEnd]);

    // All non-date filtering happens client-side so it's instant
    const filteredRecords = useMemo(() => {
        let result = records;

        if (filters.equipmentCategoryId) {
            result = result.filter((r) => r.equipmentCategoryId === filters.equipmentCategoryId);
        }
        if (filters.unitId) {
            result = result.filter((r) => r.unitId === filters.unitId);
        }
        if (filters.regionId) {
            const regionLocationIds = new Set(
                companyLocations.filter((loc) => loc.regionalDivisionId === filters.regionId).map((loc) => loc.id),
            );
            result = result.filter((r) => r.locationId && regionLocationIds.has(r.locationId));
        }
        if (filters.locationId) {
            result = result.filter((r) => r.locationId === filters.locationId);
        }

        if (filters.dateStart) {
            result = result.filter((r) => !r.createdAt || r.createdAt >= filters.dateStart!);
        }
        if (filters.dateEnd) {
            const endBound = `${filters.dateEnd}T23:59:59.999Z`;
            result = result.filter((r) => !r.createdAt || r.createdAt <= endBound);
        }

        return result;
    }, [
        records,
        filters.equipmentCategoryId,
        filters.unitId,
        filters.regionId,
        filters.locationId,
        filters.dateStart,
        filters.dateEnd,
        companyLocations,
    ]);

    const renderStatusCounts = useCallback(
        (record: NormalizedReportRecord) => (
            <div className="inspection-status-counts">
                <span className="inspection-count-badge ok">{record.okCount}</span>
                <span className="inspection-count-badge warn">{record.warnCount}</span>
                <span className="inspection-count-badge inop">{record.inopCount}</span>
            </div>
        ),
        [],
    );

    const columns = useMemo<DataTableColumn<NormalizedReportRecord>[]>(() => {
        const nextColumns: DataTableColumn<NormalizedReportRecord>[] = [
            {
                id: 'date',
                header: 'Date',
                sortable: true,
                sortType: 'numeric',
                sortValue: (record) => getDateSortValue(record.createdAt),
                mobileCardPrimary: true,
                renderCell: (record) => formatDate(record.createdAt),
            },
        ];

        if (!unitScope) {
            nextColumns.push(
                {
                    id: 'equipmentCategory',
                    header: 'Equipment Category',
                    sortable: true,
                    sortType: 'alpha',
                    sortValue: (record) => record.equipmentCategoryName ?? '',
                    mobileCardStacked: true,
                    renderCell: (record) => record.equipmentCategoryName || '—',
                },
                {
                    id: 'unit',
                    header: 'Unit',
                    sortable: true,
                    sortType: 'alpha',
                    sortValue: (record) => record.unitName ?? '',
                    mobileCardStacked: true,
                    renderCell: (record) => record.unitName || '—',
                },
            );
        }

        nextColumns.push(
            {
                id: 'inspector',
                header: 'Inspector',
                sortable: true,
                sortType: 'alpha',
                sortValue: (record) => record.userName ?? '',
                mobileCardStacked: true,
                renderCell: (record) => record.userName || '—',
            },
            {
                id: 'statusCounts',
                header: 'OK / Warn / Inop',
                sortable: true,
                sortType: 'numeric',
                sortValue: (record) => record.okCount + record.warnCount + record.inopCount,
                thClassName: 'inspection-reports-col-status-counts',
                mobileCardStacked: true,
                renderCell: renderStatusCounts,
            },
            {
                id: 'duration',
                header: 'Duration',
                sortable: true,
                sortType: 'numeric',
                sortValue: (record) => record.inspectionDurationSeconds ?? 0,
                mobileCardStacked: true,
                renderCell: (record) => formatDuration(record.inspectionDurationSeconds),
            },
        );

        return nextColumns;
    }, [renderStatusCounts, unitScope]);

    const sortedRecords = useMemo(() => {
        const column = columns.find((entry) => entry.id === sortColumnId);
        if (!column?.sortable || !column.sortValue) {
            return filteredRecords;
        }

        const sortType = column.sortType ?? 'alpha';
        return [...filteredRecords].sort((a, b) =>
            compareReportRows(a, b, column.sortValue!, sortType, sortDirection),
        );
    }, [columns, filteredRecords, sortColumnId, sortDirection]);

    const totalPages = Math.max(1, Math.ceil(sortedRecords.length / pageSize));
    const safePage = Math.min(currentPage, totalPages);
    const paginatedRecords = sortedRecords.slice((safePage - 1) * pageSize, safePage * pageSize);

    // Reset page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [filters]);

    const handleFilterChange = (newFilters: ReportFilterValues) => {
        setFilters(newFilters);
    };

    const handleRowClick = useCallback(
        (record: NormalizedReportRecord) => {
            const source = record.source === 'LegacyReport' ? 'LegacyReport' : 'InspectionReport';
            const returnState: ReportDetailReturnState = unitScope
                ? {
                      returnTo: buildUnitReportsPath(unitScope.unitId, {
                          tab: 'inspection',
                          equipmentCategoryId: unitScope.equipmentCategoryId,
                      }),
                      returnLabel: 'Back to Reports',
                  }
                : {
                      returnTo: buildCompanyReportsPath({ tab: 'inspection' }),
                      returnLabel: 'Back to Reports',
                  };
            navigate(
                buildReportPath({
                    id: record.id,
                    source,
                    slugLabel: record.unitName || record.equipmentCategoryName,
                }),
                { state: returnState },
            );
        },
        [navigate, unitScope],
    );

    const handlePageSizeChange = (newSize: number) => {
        setPageSize(newSize);
        setCurrentPage(1);
    };

    // Build export data from filtered records
    const exportData: AggregatedDataPoint[] = filteredRecords.map((r) => ({
        groupKey: r.id,
        groupLabel: r.equipmentCategoryName || r.id,
        value: r.okCount + r.warnCount + r.inopCount,
        count: 1,
    }));

    const exportConfig: ReportConfiguration = {
        metricKey: 'inspection-reports',
        dateRangeStart: filters.dateStart || '',
        dateRangeEnd: filters.dateEnd || '',
        groupByField: 'asset',
        filters: [],
        visualizationType: 'table',
    };

    if (isLoading) {
        return <div className="inspection-reports-loading">Loading inspection reports...</div>;
    }

    if (error) {
        return (
            <div className="inspection-reports-empty">
                <div className="inspection-reports-empty-title">{error}</div>
                <button
                    className="inspection-pagination-btn"
                    style={{ marginTop: 12 }}
                    onClick={() => setFilters({ ...filters })}
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="inspection-reports-tab" ref={tableRef}>
            <ReportFilterBar
                onFilterChange={handleFilterChange}
                companyId={companyId}
                initialFilters={{ dateStart: defaultStart, ...unitScope }}
                storageKey={STORAGE_KEY}
                scopeFilters={unitScope}
            />

            <div className="inspection-reports-header">
                <h3>Inspection Reports ({filteredRecords.length})</h3>
                <div className="inspection-reports-header-actions">
                    <ReportExportMenu
                        data={exportData}
                        config={exportConfig}
                        reportName="Inspection Reports"
                        targetRef={tableRef}
                    />
                </div>
            </div>

            <DataTable
                caption="Inspection Reports"
                captionVisuallyHidden
                tableClassName="inspection-reports-table"
                scrollRegionLabel="Inspection reports table"
                columns={columns}
                rows={paginatedRecords}
                getRowKey={(record) => record.id}
                defaultSortColumnId={DEFAULT_SORT_COLUMN_ID}
                defaultSortDirection="desc"
                sortColumnId={sortColumnId}
                sortDirection={sortDirection}
                onSortChange={(columnId, direction) => {
                    setSortColumnId(columnId);
                    setSortDirection(direction);
                    setCurrentPage(1);
                }}
                onRowClick={handleRowClick}
                emptyState={
                    <div className="inspection-reports-empty">
                        <div className="inspection-reports-empty-icon" aria-hidden="true">
                            <IconReports />
                        </div>
                        <div className="inspection-reports-empty-title">No inspection reports found</div>
                        <div className="inspection-reports-empty-text">
                            Try adjusting your filters or check back later.
                        </div>
                    </div>
                }
            />

            {sortedRecords.length > 0 && (
                <div className="inspection-reports-pagination">
                    <div className="inspection-pagination-info">
                        Showing {(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, sortedRecords.length)} of{' '}
                        {sortedRecords.length}
                        <select
                            className="inspection-page-size-select"
                            value={pageSize}
                            onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                            aria-label="Page size"
                        >
                            {PAGE_SIZE_OPTIONS.map((size) => (
                                <option key={size} value={size}>
                                    {size} per page
                                </option>
                            ))}
                        </select>
                    </div>
                    {totalPages > 1 && (
                        <Pagination
                            className="inspection-pagination"
                            currentPage={safePage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                            ariaLabel="Inspection reports pagination"
                        />
                    )}
                </div>
            )}
        </div>
    );
};
